import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type PublicClip } from '@air/api';

import { AssetVersion } from '~/components/v2/components/asset-modal/desktop/versions/asset-version/AssetVersion';
import { AssetVersionBadge } from '~/components/v2/components/asset-modal/desktop/versions/asset-version/AssetVersionBadge';
import { AssetVersionInfo } from '~/components/v2/components/asset-modal/desktop/versions/asset-version/AssetVersionInfo';
import { AssetVersionThumbnail } from '~/components/v2/components/asset-modal/desktop/versions/asset-version/AssetVersionThumbnail';
import { AssetVersionThumbnailPreview } from '~/components/v2/components/asset-modal/desktop/versions/asset-version/AssetVersionThumbnailPreview';
import { getVersionMeta } from '~/components/v2/utils/versions/getVersionMeta';
import { QueryParamNames } from '~/constants/search';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { useSwitchPublicVersion } from '~/swr-hooks/versions/useSwitchPublicVersion';

export type PublicVersionsListItemProps = {
  version: PublicClip;
};

export const PublicVersionsListItem = memo(({ version }: PublicVersionsListItemProps) => {
  const clipId = useSelector(centralizedClipIdSelector);
  const { switchVersion } = useSwitchPublicVersion();

  const meta = useMemo(() => {
    return getVersionMeta(version).join(' · ');
  }, [version]);

  const handleClick = useCallback(() => {
    switchVersion({
      version,
      location: 'tab',
      query: {
        [QueryParamNames.timestamp]: null,
        [QueryParamNames.discussionId]: null,
      },
    });
  }, [switchVersion, version]);

  return (
    <AssetVersion
      data-version={version.version}
      data-version-id={version.id}
      data-version-status={version.status}
      data-version-type="public"
      state={clipId === version.id ? 'active' : 'default'}
      onClick={handleClick}
    >
      <AssetVersionThumbnail>
        <AssetVersionBadge version={version.version ?? 0} />
        <AssetVersionThumbnailPreview version={version} />
      </AssetVersionThumbnail>
      <AssetVersionInfo title={version.title || version.importedName || ''} meta={meta} />
    </AssetVersion>
  );
});

PublicVersionsListItem.displayName = 'PublicVersionsListItem';
